import { pbkdf2Sync, createCipheriv, createDecipheriv, Buffer } from "browser-crypto";
import { Cipher } from "crypto";

/**
 * Generates a hashed value using PBKDF2 with a specified input and optional salt.
 *
 * @param {string} input - The input string to be hashed.
 * @param {string | undefined} salt - Optional salt value. If not provided, a default salt is used.
 * @returns {string} - The hashed value as a hexadecimal string.
 *
 * @throws {Error} - If there is an error during the hashing process.
 * @returns {string} - JSON-formatted string with an error message if hashing fails.
 */

const algoParams = {
  CRYPTO_KEY: import.meta.env.VITE_CRYPTO_KEY,
  CRYPTO_SALTSIZE: import.meta.env.VITE_CRYPTO_SALTSIZE,
  CRYPTO_IVSIZE: import.meta.env.VITE_CRYPTO_IVSIZE,
  CRYPTO_KEYSIZE: import.meta.env.VITE_CRYPTO_KEYSIZE,
  CRYPTO_ITERATIONS: import.meta.env.VITE_CRYPTO_ITERATIONS,
};

const generateHash = (input: string, salt: Buffer | undefined): Buffer | string => {
  if (salt === void 0) {
    salt = undefined;
  }
  let hashed: Buffer | string = "";
  try {
    const hashedBuffer = pbkdf2Sync(
      input,
      salt ? salt : algoParams.CRYPTO_KEY,
      parseInt(algoParams.CRYPTO_ITERATIONS),
      parseInt(algoParams.CRYPTO_KEYSIZE) * 4,
      "sha256"
    );

    // const derivedKey = createHash('sha256').update(hashedBuffer).digest();

    hashed = salt ? hashedBuffer : hashedBuffer.toString("hex");
  } catch (err) {
    console.error("Error while hashing", err);
  }
  return hashed;
};

/**
 * Encrypts a given input using AES-256-CBC encryption.
 *
 * @param {string} input - The input string to be encrypted.
 * @returns {string} - The base64-encoded string containing the encrypted data.
 *
 * @throws {Error} - If there is an error during the encryption process.
 * @returns {string} - JSON-formatted string with an error message if encryption fails.
 */
export const encrypt = (input: string): string => {
  let combinedStr: string = "";
  try {
    // Generate random salt
    const salt: Buffer = Buffer.from(window.crypto.getRandomValues(new Uint8Array(parseInt(algoParams.CRYPTO_SALTSIZE)))); //randomBytes(parseInt(algoParams.CRYPTO_SALTSIZE));
    //console.log(`salt : ${arrayBufferToHexString(salt)}`);
    // Generate key using the getHash function
    const key: Buffer | string = generateHash(algoParams.CRYPTO_KEY, salt);
    //console.log(`key : ${arrayBufferToHexString(key)}`);
    // Generate random iv
    const iv: Buffer = Buffer.from(window.crypto.getRandomValues(new Uint8Array(parseInt(algoParams.CRYPTO_IVSIZE)))); //randomBytes(parseInt(algoParams.CRYPTO_IVSIZE));
    //console.log(`iv : ${arrayBufferToHexString(iv)}`);

    // Create AES cipher using crypto module
    const cipher: Cipher = createCipheriv("aes-256-cbc", key, iv);
    // Encrypt data
    const encrypted: Buffer = Buffer.concat([cipher.update(input, "utf8"), cipher.final()]);

    // Combine bytes of salt + iv + encrypted
    combinedStr = Buffer.concat([salt, iv, encrypted]).toString("base64");
    //combinedStr = btoa(mergeCrypto(salt, iv, encrypted));
  } catch (err) {
    console.error("Error while encrypting", err);
  }
  return combinedStr;
};

/**
 * Decrypts a given input using AES-256-CBC encryption.
 *
 * @param {string} input - The base64-encoded input string to be decrypted.
 * @returns {string} - The decrypted UTF-8 string.
 *
 * @throws {Error} - If there is an error during the decryption process.
 * @returns {string} - JSON-formatted string with an error message if decryption fails.
 */
export const decrypt = (input: string): string => {
  let decryptedString = "";
  try {
    // Convert base64 input to Buffer
    const inputBuffer: Buffer = Buffer.from(input, "base64");
    // Split salt, iv, and encrypted text from input Buffer
    const salt: Buffer = inputBuffer.slice(0, parseInt(algoParams.CRYPTO_SALTSIZE));
    const iv: Buffer = inputBuffer.slice(parseInt(algoParams.CRYPTO_SALTSIZE), parseInt(algoParams.CRYPTO_SALTSIZE) + parseInt(algoParams.CRYPTO_IVSIZE));
    const encrypted: Buffer = inputBuffer.slice(parseInt(algoParams.CRYPTO_SALTSIZE) + parseInt(algoParams.CRYPTO_IVSIZE));

    // Generate key using the getHash function
    const key: Buffer | string = generateHash(algoParams.CRYPTO_KEY, salt);
    // Create AES decipher using crypto module
    const decipher: Cipher = createDecipheriv("aes-256-cbc", key, iv);
    // Decrypt data
    const decryptedBuffer: Buffer = Buffer.concat([decipher.update(encrypted), decipher.final()]);

    // Convert decrypted Buffer to UTF-8 string
    decryptedString = decryptedBuffer.toString("utf8");
  } catch (err: unknown) {
    console.error("Error while decrypting", err);
    return JSON.stringify({
      status: "error",
      message: (err as Error)?.message,
      exception: (err as Error)?.stack,
    });
  }
  return decryptedString;
};
